// Build clean, paste-ready HTML from an already-rendered message body, for the
// rich (text/html) half of a copy-to-clipboard. Pasting this into Docs / Word /
// Slack / Gmail yields a formatted table + bold + lists, while the text/plain half
// (the markdown source) still serves code editors and other LLMs. See CopyButton.vue.
//
// Two things have to be cleaned out of the live DOM before it's clipboard-worthy:
//
//  1. KaTeX. markstream renders each equation TWICE - a visual `.katex-html` pile of
//     absolutely-positioned spans plus a hidden `.katex-mathml`. Copying the raw HTML
//     pastes the equation twice (the well-known KaTeX copy bug), and the positioned
//     spans paste as gibberish into Docs/Word. We collapse each `.katex` to its LaTeX
//     source (from the MathML `<annotation>`), wrapped `\(...\)` / `\[...\]` - the same
//     thing KaTeX's own copy-tex extension does for selection copy.
//  2. Interactive chrome. Code blocks carry a header with a "Copy" button, etc. Those
//     `<button>`s are affordances, not content, so they're stripped.

/** Collapse rendered KaTeX + strip interactive chrome; return paste-ready innerHTML. */
export function richHtmlFromElement(el: HTMLElement): string {
  if (typeof document === 'undefined') return el.innerHTML;
  const clone = el.cloneNode(true) as HTMLElement;

  // KaTeX -> its LaTeX source (single, readable copy instead of a doubled render).
  clone.querySelectorAll('.katex').forEach((node) => {
    const tex = node.querySelector('annotation[encoding="application/x-tex"]')?.textContent?.trim();
    const display = node.closest('.katex-display') != null;
    const text = tex
      ? (display ? `\\[${tex}\\]` : `\\(${tex}\\)`)
      : (node.textContent ?? '');
    node.replaceWith(document.createTextNode(text));
  });
  // Any bare MathML left behind (defensive - the .katex pass usually owns it all).
  clone.querySelectorAll('math').forEach((m) => m.remove());
  // Code-block chrome: the header bar carries a language label + copy button (and an
  // optional caption), none of it content - drop the whole header (a sibling of the
  // <pre>, so the code itself survives), then any stray buttons elsewhere.
  clone.querySelectorAll('.code-block-header').forEach((h) => h.remove());
  clone.querySelectorAll('button').forEach((b) => b.remove());

  return clone.innerHTML;
}
